import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { TONE_TEXT } from '../tone';
import { type ChartLayout } from './layout';
import { percentOf } from './scale';

export type SmallMultipleTile = {
  id: string;
  label: ReactNode;
  values: number[];
  /** The latest value, formatted. */
  display: ReactNode;
  /** The change in words ("Up 277 in 5 weeks"). */
  change: ReactNode;
  /** Whether the change helps the reader, never its direction. */
  sentiment?: 'positive' | 'attention' | 'neutral';
};

export type SmallMultiplesProps = {
  tiles: SmallMultipleTile[];
  /**
   * `shared` (default) draws every tile on one scale from zero, so sizes compare; `indexed` starts
   * every tile at 100, so shapes compare. Say which in the frame's subtitle.
   */
  scale?: 'shared' | 'indexed';
  /** `auto` (default) switches to the narrow form in a narrow container: two columns of compact tiles with sparkline-height plots. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

const GRID: Record<ChartLayout, string> = {
  auto: 'grid-cols-2 @xl:grid-cols-3',
  narrow: 'grid-cols-2',
  wide: 'grid-cols-1 @sm:grid-cols-2 @xl:grid-cols-3',
};
const TILE: Record<ChartLayout, string> = { auto: 'p-2 @md:p-3', narrow: 'p-2', wide: 'p-3' };
const PLOT: Record<ChartLayout, string> = { auto: 'h-8 @md:h-12', narrow: 'h-8', wide: 'h-12' };

/** Direction of a tile's series, so the change never relies on its sign or hue alone. */
const arrowOf = (values: number[]) => {
  const first = values[0];
  const last = values[values.length - 1];
  if (first === undefined || last === undefined || first === last) return null;
  return last > first ? ArrowUp : ArrowDown;
};

/** The same small line repeated per category: many trends at a glance. Two, then three columns by width. */
export function SmallMultiples({
  tiles,
  scale = 'shared',
  layout = 'auto',
  description,
  className,
}: SmallMultiplesProps) {
  const series = tiles.map((t) =>
    scale === 'indexed' && t.values[0] ? t.values.map((v) => (v / t.values[0]) * 100) : t.values,
  );
  const all = series.flat();
  const lo = scale === 'indexed' ? Math.min(...all) : 0;
  const hi = Math.max(...all);
  return (
    <div role="img" aria-label={description} className={cx('@container', className)}>
      <div className={cx('grid gap-2', GRID[layout])}>
        {tiles.map((t, k) => (
          <div
            key={t.id}
            className={cx('flex flex-col gap-1 rounded-jc-info bg-jc-elevated', TILE[layout])}
            aria-hidden="true"
          >
            <span className="flex items-baseline justify-between gap-2">
              <span className="truncate type-subheading text-jc-ink">{t.label}</span>
              <span className="font-display text-sm font-bold text-jc-ink tabular-nums">{t.display}</span>
            </span>
            <span className={cx('relative block border-b border-jc-chart-grid', PLOT[layout])}>
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 size-full overflow-visible"
              >
                <polyline
                  points={series[k]
                    .map((v, i) => `${(i / Math.max(1, series[k].length - 1)) * 100},${100 - percentOf(v, lo, hi)}`)
                    .join(' ')}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                  strokeWidth={2}
                  strokeLinejoin="round"
                  className="stroke-jc-ink"
                />
              </svg>
            </span>
            <span
              className={cx(
                'flex items-center gap-1 text-xs font-semibold',
                t.sentiment && t.sentiment !== 'neutral' ? TONE_TEXT[t.sentiment] : 'text-jc-ink-secondary',
              )}
            >
              {arrowOf(t.values) ? (
                <Icon icon={arrowOf(t.values)!} size="sm" weight="bold" className="shrink-0" />
              ) : null}
              <span>{t.change}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
